import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useRestClient } from "../../../core/api/RestClientContext";
import {
    EditableNotificationItem,
    NotificationChannel,
    TelegramNotificationPayload,
} from "../../../core/models/notification";
import { Button } from "../../../core/components/Button";
import {
    buildEditPayload,
    parseEditedContent,
    stringifyEditableContent,
} from "./sent-notification-payload";

const formatDate = (raw?: string): string => {
    if (!raw) {
        return "—";
    }

    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) {
        return raw;
    }

    return date.toLocaleString("ru-RU", {
        dateStyle: "short",
        timeStyle: "short",
    });
};

const summarize = (notification: EditableNotificationItem["notification"]): string => {
    if (!notification) {
        return "Нет текста";
    }

    const payload = notification as TelegramNotificationPayload;
    const blocks = Array.isArray(payload.blocks) ? payload.blocks : [];
    const textBlock = blocks.find((block) => block.type === "TEXT" && Boolean(block.text));
    if (textBlock && "text" in textBlock && textBlock.text) {
        return textBlock.text;
    }

    const mediaBlock = blocks.find((block) => "caption" in block && Boolean(block.caption));
    if (mediaBlock && "caption" in mediaBlock && mediaBlock.caption) {
        return mediaBlock.caption;
    }

    const subject = "subject" in notification ? notification.subject : undefined;
    if (subject) {
        return subject;
    }

    return `${blocks.length} блоков`;
};

const channelLabel: Record<NotificationChannel, string> = {
    TELEGRAM: "Telegram",
    EMAIL: "Email",
};

export const SentNotificationsPanel: React.FC = () => {
    const restClient = useRestClient();
    const [items, setItems] = useState<EditableNotificationItem[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [drafts, setDrafts] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState<string | null>(null);

    useEffect(() => {
        const fetchHistory = async () => {
            setLoading(true);
            try {
                const page = await restClient.listNotifications({ page: 0, size: 8 });
                const content = page.content || [];
                setItems(content);
                setDrafts(Object.fromEntries(
                    content.map((item) => [item.id, stringifyEditableContent(item.notification)]),
                ));
                if (content.length > 0) {
                    setSelectedId((current) => current ?? content[0].id);
                }
            } catch (error) {
                console.error("Failed to load notifications history", error);
                toast.error("Не удалось загрузить историю уведомлений");
            } finally {
                setLoading(false);
            }
        };

        fetchHistory();
    }, [restClient]);

    const selected = useMemo(
        () => items.find((item) => item.id === selectedId) ?? items[0] ?? null,
        [items, selectedId],
    );

    const draft = selected ? drafts[selected.id] ?? stringifyEditableContent(selected.notification) : "";
    const parsedDraft = selected ? parseEditedContent(draft) : null;
    const jsonError = parsedDraft && !parsedDraft.ok ? parsedDraft.error : null;
    const isDirty = Boolean(
        selected && draft !== stringifyEditableContent(selected.notification),
    );

    const handleDraftChange = (itemId: string, nextDraft: string) => {
        setDrafts((current) => ({ ...current, [itemId]: nextDraft }));
    };

    const handleEdit = async (item: EditableNotificationItem) => {
        const parsed = parseEditedContent(drafts[item.id] ?? stringifyEditableContent(item.notification));
        if (!parsed.ok) {
            toast.error(parsed.error);
            return;
        }

        const payload = buildEditPayload(item.notification, parsed.value);
        setSavingId(item.id);
        try {
            const response = await restClient.editNotification(item.id, payload, [item.channel]);
            if (response.success) {
                toast.success("Уведомление обновлено");
                setItems((current) => current.map((entry) => (
                    entry.id === item.id
                        ? { ...entry, notification: payload as EditableNotificationItem["notification"] }
                        : entry
                )));
                setDrafts((current) => ({
                    ...current,
                    [item.id]: stringifyEditableContent(payload),
                }));
            } else {
                toast.error(response.error || response.message || "Не удалось обновить уведомление");
            }
        } catch (error) {
            console.error("Failed to update notification", error);
            toast.error("Не удалось обновить уведомление");
        } finally {
            setSavingId(null);
        }
    };

    return (
        <div className="sent-notifications-panel">
            <div className="notification-sender-header">
                <h3>Отправленные уведомления</h3>
            </div>

            {loading ? (
                <div className="notification-placeholder">Загрузка истории…</div>
            ) : items.length === 0 ? (
                <div className="notification-placeholder">Пока нет отправленных уведомлений</div>
            ) : (
                <>
                    <div className="sent-notifications-list">
                        {items.map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                className={`sent-notification-item ${selected?.id === item.id ? "selected" : ""}`}
                                onClick={() => setSelectedId(item.id)}
                            >
                                <div className="sent-notification-item-header">
                                    <strong>{channelLabel[item.channel] || item.channel}</strong>
                                    <span>{formatDate(item.ts)}</span>
                                </div>
                                <div className="sent-notification-item-text">{summarize(item.notification)}</div>
                            </button>
                        ))}
                    </div>

                    {selected && (
                        <div className="sent-notification-detail">
                            <div className="sent-notification-detail-header">
                                <div>
                                    <div className="sent-notification-detail-meta">
                                        {channelLabel[selected.channel]} · {formatDate(selected.ts)}
                                    </div>
                                    <div className="sent-notification-detail-hint">
                                        Редактируйте содержимое (текст, parseMode, блоки). Id, метаданные и вложения скрыты.
                                    </div>
                                </div>
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={() => handleEdit(selected)}
                                    disabled={savingId === selected.id || Boolean(jsonError) || !isDirty}
                                >
                                    {savingId === selected.id ? "Сохранение…" : "Сохранить"}
                                </Button>
                            </div>
                            <textarea
                                className={`sent-notification-json-editor ${jsonError ? "invalid" : ""}`}
                                value={draft}
                                onChange={(event) => handleDraftChange(selected.id, event.target.value)}
                                spellCheck={false}
                                aria-label="JSON содержимого уведомления"
                            />
                            {jsonError && (
                                <div className="sent-notification-json-error">{jsonError}</div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
